import { Buffer } from 'buffer'
import React, { useState, useEffect, useRef } from 'react';
import { Send, Twitter, Wallet, CheckCircle, AlertCircle, Copy, Clock, User, DollarSign } from 'lucide-react';
import { 
  Connection,
  PublicKey, 
  SystemProgram, 
  Transaction,
  LAMPORTS_PER_SOL
} from '@solana/web3.js';

window.Buffer = Buffer;

// Updated RPC endpoints with your Alchemy API key (CORS-friendly)
const RPC_ENDPOINTS = [
  'https://solana-mainnet.g.alchemy.com/v2/BS5TDLhPfQM5KzH4LGVpz', // Your Alchemy endpoint (primary)
  'https://solana.publicnode.com',                                   // Backup
  'https://rpc.hellomoon.io/mainnet',                               // Backup
  'https://api.mainnet-beta.solana.com',                            // Last resort
  'https://solana-api.projectserum.com'                             // Additional backup
];

// Function to get blockhash with minimal logging (only when needed)
const getReliableBlockhash = async () => {
  const errors = [];
  
  for (const endpoint of RPC_ENDPOINTS) {
    try {
      const tempConnection = new Connection(endpoint, 'confirmed');
      
      // Set a timeout for the RPC call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('RPC timeout')), 8000)
      );
      
      const blockhashPromise = tempConnection.getLatestBlockhash('confirmed');
      
      const { blockhash } = await Promise.race([blockhashPromise, timeoutPromise]);
      console.log(`✅ Got blockhash from RPC fallback: ${endpoint}`);
      return blockhash;
    } catch (error) {
      errors.push(`${endpoint}: ${error.message}`);
      // Don't log individual RPC failures - only log if all fail
      continue;
    }
  }
  
  console.error('❌ All RPC endpoints failed for manual blockhash:', errors);
  throw new Error(`All RPC endpoints failed: ${errors.join(', ')}`);
};

// Simplified Phantom wallet integration (with reliable blockhash)
const usePhantomWallet = () => {
  const [connected, setConnected] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [phantom, setPhantom] = useState(null);

  useEffect(() => {
    const getPhantom = () => {
      if ('solana' in window) {
        const provider = window.solana;
        if (provider.isPhantom) {
          setPhantom(provider);
          return provider;
        }
      }
      return null;
    };

    const phantom = getPhantom();
    if (phantom) {
      phantom.connect({ onlyIfTrusted: true }).then(({ publicKey }) => {
        setConnected(true);
        setPublicKey(publicKey.toString());
      }).catch(() => {
        // Not connected, that's fine
      });
    }
  }, []);

  const connect = async () => {
    if (!phantom) {
      alert('Phantom wallet not found! Please install Phantom wallet extension.');
      return;
    }

    setConnecting(true);
    try {
      const response = await phantom.connect();
      setConnected(true);
      setPublicKey(response.publicKey.toString());
    } catch (error) {
      console.error('Phantom connection failed:', error);
      alert('Failed to connect to Phantom wallet');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (phantom) {
      await phantom.disconnect();
    }
    setConnected(false);
    setPublicKey(null);
  };

  const signAndSendTransaction = async (transaction) => {
    if (!phantom || !connected) {
      throw new Error('Phantom wallet not connected');
    }

    try {
      console.log('🔄 Attempting transaction with Phantom auto-handling...');
      
      // APPROACH 1: Let Phantom handle everything (preferred method)
      try {
        const { signature } = await phantom.signAndSendTransaction(transaction);
        console.log('✅ Transaction sent via Phantom auto-handling:', signature.slice(0, 8) + '...');
        return signature;
      } catch (phantomError) {
        // Only try RPC fallback for specific blockhash errors
        if (phantomError.message.includes('recentBlockhash') || 
            phantomError.message.includes('blockhash') ||
            phantomError.message.includes('recent_blockhash')) {
          
          console.log('⚠️ Phantom needs manual blockhash, trying RPC fallback...');
          
          // APPROACH 2: Manual blockhash with RPC fallback (silent errors)
          const blockhash = await getReliableBlockhash();
          
          // Set required transaction properties
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = new PublicKey(publicKey);
          
          console.log('🔄 Retrying transaction with manual blockhash...');
          
          const { signature } = await phantom.signAndSendTransaction(transaction);
          console.log('✅ Transaction sent via manual blockhash:', signature.slice(0, 8) + '...');
          return signature;
        } else {
          // Re-throw if it's not a blockhash issue
          throw phantomError;
        }
      }
    } catch (error) {
      console.error('Transaction failed:', error);
      throw error;
    }
  };

  return { 
    connected, 
    publicKey, 
    connecting, 
    connect, 
    disconnect, 
    signAndSendTransaction,
    isPhantomInstalled: !!phantom,
    phantom
  };
};

// Production API service
const apiService = {
  baseUrl: process.env.REACT_APP_API_URL || 'https://solana-escrow-sandy.vercel.app',
  
  async request(endpoint, options = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      console.log('🔄 API Request:', url);
      
      const config = {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      if (options.token) {
        config.headers.Authorization = `Bearer ${options.token}`;
      }

      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  },

  async getTwitterAuthUrl() {
    return this.request('/auth/twitter');
  },

  async handleTwitterCallback(code, state, mockUsername = null) {
    const payload = { code, state };
    if (mockUsername) {
      payload.mock_username = mockUsername;
    }
    return this.request('/auth/twitter/callback', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async createClaim(data) {
    return this.request('/api/claims', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getClaims(token) {
    return this.request('/api/claims', { token });
  },

  async requestWithdrawal(claimId, withdrawalAddress, token) {
    return this.request(`/api/claims/${claimId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify({ withdrawalAddress }),
      token,
    });
  },

  async updateClaim(data, token) {
    return this.request('/api/claims/update', {
      method: 'PUT',
      body: JSON.stringify(data),
      token,
    });
  },

  async getUserProfile(token) {
    return this.request('/api/user/profile', { token });
  },

  async updateWalletAddress(walletAddress, token) {
    return this.request('/api/user/wallet', {
      method: 'PUT',
      body: JSON.stringify({ walletAddress }),
      token,
    });
  }
};

// Fee calculation utility
const calculateFees = (amount) => {
  const originalAmount = parseFloat(amount) || 0;
  const developerFee = originalAmount * 0.01; // 1% fee
  const recipientAmount = originalAmount - developerFee;
  
  return {
    originalAmount,
    developerFee,
    recipientAmount,
    feePercentage: 1.0
  };
};

// Fee Breakdown Component
const FeeBreakdown = ({ amount, className = "" }) => {
  const fees = calculateFees(amount);
  
  if (!amount || fees.originalAmount <= 0) {
    return null;
  }

  return (
    <div className={`bg-blue-900 bg-opacity-50 rounded-lg p-4 border border-blue-700 ${className}`}>
      <h4 className="text-sm font-semibold text-blue-300 mb-3 flex items-center">
        <DollarSign size={16} className="mr-2" />
        Transaction Breakdown
      </h4>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-gray-300">Amount you send:</span>
          <span className="text-white font-mono">{fees.originalAmount.toFixed(4)} SOL</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-300">Developer fee (1%):</span>
          <span className="text-yellow-400 font-mono">{fees.developerFee.toFixed(4)} SOL</span>
        </div>
        
        <div className="border-t border-blue-600 pt-2 mt-2">
          <div className="flex justify-between items-center font-semibold">
            <span className="text-green-300">Recipient receives:</span>
            <span className="text-green-400 font-mono">{fees.recipientAmount.toFixed(4)} SOL</span>
          </div>
        </div>
      </div>
      
      <div className="mt-3 text-xs text-gray-400">
        The 1% developer fee supports platform development and maintenance.
      </div>
    </div>
  );
};

const SolanaTwitterClaimer = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [sendAmount, setSendAmount] = useState('');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [claims, setClaims] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [withdrawalAddress, setWithdrawalAddress] = useState('');
  const [authToken, setAuthToken] = useState(() => {
    try {
      return localStorage.getItem('auth_token');
    } catch {
      return null;
    }
  });

  // Prevent duplicate callback processing
  const callbackProcessedRef = useRef(false);

  const wallet = usePhantomWallet();

  // Debug: Show current API URL
  useEffect(() => {
    console.log('🔧 Frontend Config:');
    console.log('API URL:', apiService.baseUrl);
    console.log('Primary RPC:', RPC_ENDPOINTS[0]);
    console.log('Environment:', process.env.REACT_APP_ENVIRONMENT);
  }, []);

  // Twitter OAuth callback handler
  useEffect(() => {
    const handleTwitterCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const mockUsername = urlParams.get('mock_username');
      
      if (callbackProcessedRef.current || !code || !state) {
        return;
      }
      
      callbackProcessedRef.current = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      
      try {
        setIsLoading(true);
        
        const response = await apiService.handleTwitterCallback(code, state, mockUsername);
        
        if (!response.success || !response.token) {
          throw new Error('Invalid response from authentication server');
        }
        
        setAuthToken(response.token);
        localStorage.setItem('auth_token', response.token);
        
        showNotification(
          `🎉 Successfully authenticated as @${response.user.username}!`, 
          'success'
        );
        
        setActiveTab('claim');
        
      } catch (error) {
        console.error('Twitter callback error:', error);
        showNotification('Twitter authentication failed: ' + error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    handleTwitterCallback();
  }, []);

  // Load user profile and claims when authenticated
  useEffect(() => {
    if (authToken) {
      loadUserProfile();
      if (activeTab === 'claim') {
        loadClaims();
      }
    }
  }, [authToken, activeTab]);

  // Auto-fill withdrawal address from wallet when connected
  useEffect(() => {
    if (wallet.connected && wallet.publicKey && !withdrawalAddress) {
      setWithdrawalAddress(wallet.publicKey);
    }
  }, [wallet.connected, wallet.publicKey, withdrawalAddress]);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 7000);
  };

  const loadUserProfile = async () => {
    try {
      const response = await apiService.getUserProfile(authToken);
      setUserProfile(response.user);
      
      if (response.user.walletAddress) {
        setWithdrawalAddress(response.user.walletAddress);
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  const loadClaims = async () => {
    try {
      console.log('🔄 Loading claims for authenticated user...');
      const response = await apiService.getClaims(authToken);
      
      setClaims(response.claims || []);
      
      if (response.claims && response.claims.length > 0) {
        showNotification(`Found ${response.claims.length} pending claim(s) for you!`, 'info');
      }
    } catch (error) {
      showNotification('Failed to load claims: ' + error.message, 'error');
      console.error('Load claims error:', error);
    }
  };

  const handleTwitterLogin = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔄 Initiating Twitter login...');
      
      const response = await apiService.getTwitterAuthUrl();
      
      if (!response.authUrl) {
        throw new Error('Invalid auth URL received from server');
      }
      
      console.log(`🐦 Twitter OAuth Mode: ${response.mode}`);
      showNotification(`${response.mode === 'real_stateless' ? 'Redirecting to Twitter...' : 'Using mock Twitter...'}`, 'info');
      
      window.location.href = response.authUrl;
      
    } catch (error) {
      console.error('Twitter login error:', error);
      showNotification('Twitter login failed: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleSendSolana = async () => {
    if (!wallet.connected || !sendAmount || !twitterHandle) {
      showNotification('Please connect wallet and fill all fields', 'error');
      return;
    }

    const amount = parseFloat(sendAmount);
    if (amount <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

    // Check minimum amount for fees
    const fees = calculateFees(amount);
    if (fees.developerFee < 0.000001) {
      showNotification('Minimum amount is 0.0001 SOL to cover fees', 'error');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`🔄 Sending ${amount} SOL to @${twitterHandle}...`);
      
      // Step 1: Create claim in database first
      const claimResponse = await apiService.createClaim({
        senderAddress: wallet.publicKey,
        twitterHandle: twitterHandle.replace('@', ''),
        amount: amount,
        senderTxSignature: 'pending'
      });

      console.log('✅ Claim created:', claimResponse.claimId);
      const claimId = claimResponse.claimId;
      
      // Step 2: Send SOL to backend's authority account (RELIABLE - Alchemy primary)
      const authorityAddress = claimResponse.authorityAddress || 'CGcsaFoQzHjrgXHKfVgd4oJwZxGruNd6HhAmftcEY4qB';
      
      console.log('💰 Creating transaction with Alchemy RPC endpoint...');
      
      // Create transaction with Alchemy RPC fallback
      const transaction = new Transaction();
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: new PublicKey(wallet.publicKey),
          toPubkey: new PublicKey(authorityAddress),
          lamports: Math.floor(amount * LAMPORTS_PER_SOL),
        })
      );
      
      console.log('💰 Sending transaction via Phantom with Alchemy endpoint...');
      
      const signature = await wallet.signAndSendTransaction(transaction);
      
      console.log('✅ Transaction completed:', signature.slice(0, 8) + '...');
      
      // Step 3: Update claim with transaction signature
      await apiService.updateClaim({
        claimId: claimId,
        senderTxSignature: signature,
        status: 'pending'
      }, authToken);
      
      showNotification(
        `✅ Successfully sent ${sendAmount} SOL to @${twitterHandle}! 
         
         💰 Amount sent: ${fees.originalAmount.toFixed(4)} SOL
         🎁 Recipient gets: ${fees.recipientAmount.toFixed(4)} SOL (after 1% fee)
         📋 Claim ID: ${claimId}
         
         @${twitterHandle} can now login and claim their SOL!`, 
        'success'
      );
      
      // Clear form
      setSendAmount('');
      setTwitterHandle('');
      
    } catch (error) {
      let errorMessage = 'Transaction failed: ' + error.message;
      
      // Provide specific guidance based on error type
      if (error.message.includes('All RPC endpoints failed')) {
        errorMessage += '\n\n🔧 This appears to be a network connectivity issue. Please try:\n1. Refresh the page and try again\n2. Check your internet connection\n3. Try using a VPN if the issue persists';
      } else if (error.message.includes('User rejected')) {
        errorMessage = 'Transaction cancelled by user';
      } else if (error.message.includes('Insufficient funds')) {
        errorMessage = 'Insufficient SOL in your wallet for this transaction';
      }
      
      showNotification(errorMessage, 'error');
      console.error('Send transaction error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawal = async (claim) => {
    if (!withdrawalAddress) {
      showNotification('Please set your withdrawal address first', 'error');
      return;
    }

    try {
      setIsLoading(true);
      console.log(`🔄 Requesting withdrawal for claim ${claim.claimId}`);
      
      const response = await apiService.requestWithdrawal(
        claim.claimId, 
        withdrawalAddress, 
        authToken
      );
      
      if (response.success) {
        showNotification(
          `🎉 Withdrawal successful! 
           
           💰 You received: ${response.amountTransferred} SOL
           📍 Transferred to: ${withdrawalAddress.slice(0, 8)}...${withdrawalAddress.slice(-8)}
           
           Check your Phantom wallet!`, 
          'success'
        );
        
        // Refresh claims
        await loadClaims();
      } else {
        throw new Error(response.error || 'Withdrawal failed');
      }
      
    } catch (error) {
      console.error('Withdrawal failed:', error);
      showNotification(`Withdrawal failed: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateWithdrawalAddress = async () => {
    if (!withdrawalAddress) {
      showNotification('Please enter a withdrawal address', 'error');
      return;
    }

    try {
      // Validate address format
      new PublicKey(withdrawalAddress);
      
      await apiService.updateWalletAddress(withdrawalAddress, authToken);
      showNotification('Withdrawal address updated successfully', 'success');
      
      // Refresh profile
      await loadUserProfile();
      
    } catch (error) {
      showNotification('Failed to update withdrawal address: ' + error.message, 'error');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      showNotification('Copied to clipboard!', 'info');
    } catch (error) {
      console.error('Copy failed:', error);
      showNotification('Copy failed', 'error');
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    setUserProfile(null);
    setWithdrawalAddress('');
    try {
      localStorage.removeItem('auth_token');
    } catch (e) {
      console.warn('Could not remove from localStorage:', e);
    }
    setClaims([]);
    showNotification('Logged out successfully', 'info');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            PayTweets
          </h1>
          <p className="text-gray-300 text-lg">
            Send SOL to Twitter users instantly - 1% fee supports platform development
          </p>
          <div className="text-sm text-gray-400 mt-2">
            ⚡ Mainnet • 1% Fee • Secure Withdrawals • Powered by Alchemy
          </div>
        </div>

        {/* Phantom Wallet Check */}
        {!wallet.isPhantomInstalled && (
          <div className="mb-6 p-4 bg-yellow-900 border border-yellow-700 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="text-yellow-400" size={16} />
              <span className="text-yellow-300 text-sm">
                Phantom wallet not detected. 
                <a 
                  href="https://phantom.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline ml-1"
                >
                  Install Phantom wallet
                </a>
              </span>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg border ${
            notification.type === 'error' 
              ? 'bg-red-900 border-red-700 text-red-300'
              : notification.type === 'success'
              ? 'bg-green-900 border-green-700 text-green-300'  
              : 'bg-blue-900 border-blue-700 text-blue-300'
          }`}>
            <div className="flex items-center space-x-2">
              {notification.type === 'error' && <AlertCircle size={16} />}
              {notification.type === 'success' && <CheckCircle size={16} />}
              <span className="text-sm whitespace-pre-line">{notification.message}</span>
              <button 
                onClick={() => setNotification(null)}
                className="ml-auto text-current hover:opacity-80"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Wallet Connection */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Wallet className="text-yellow-400" size={24} />
              <div>
                <span className="text-white font-semibold block">
                  {wallet.connected ? 'Phantom Wallet Connected' : 'Connect Phantom Wallet'}
                </span>
                {wallet.connected && (
                  <div className="text-gray-300 text-sm font-mono">
                    {wallet.publicKey?.slice(0, 6)}...{wallet.publicKey?.slice(-4)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              {!wallet.connected ? (
                <button
                  onClick={wallet.connect}
                  disabled={wallet.connecting || !wallet.isPhantomInstalled}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200"
                >
                  {wallet.connecting ? 'Connecting...' : 'Connect Phantom'}
                </button>
              ) : (
                <button
                  onClick={wallet.disconnect}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200"
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white bg-opacity-10 backdrop-blur-lg rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('send')}
            className={`flex-1 py-3 px-4 rounded-md font-semibold transition-all duration-200 ${
              activeTab === 'send'
                ? 'bg-white text-purple-900'
                : 'text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            <Send className="inline mr-2" size={18} />
            Send SOL
          </button>
          <button
            onClick={() => setActiveTab('claim')}
            className={`flex-1 py-3 px-4 rounded-md font-semibold transition-all duration-200 ${
              activeTab === 'claim'
                ? 'bg-white text-purple-900'
                : 'text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            <Twitter className="inline mr-2" size={18} />
            Claim SOL
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-3 px-4 rounded-md font-semibold transition-all duration-200 ${
              activeTab === 'profile'
                ? 'bg-white text-purple-900'
                : 'text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            <User className="inline mr-2" size={18} />
            Profile
          </button>
        </div>

        {/* Send Tab */}
        {activeTab === 'send' && (
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Send Solana to Twitter User</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Twitter Handle
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">@</span>
                  <input
                    type="text"
                    value={twitterHandle}
                    onChange={(e) => setTwitterHandle(e.target.value)}
                    placeholder="username"
                    className="w-full pl-8 pr-4 py-3 bg-white bg-opacity-20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Amount (SOL)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 bg-white bg-opacity-20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <div className="text-sm text-gray-400 mt-1">
                  Powered by Alchemy RPC - Fast & Reliable
                </div>
              </div>

              {/* Fee Breakdown */}
              <FeeBreakdown amount={sendAmount} />

              <button
                onClick={handleSendSolana}
                disabled={!wallet.connected || !sendAmount || !twitterHandle || isLoading}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200"
              >
                {isLoading ? 'Sending...' : `Send ${sendAmount || '0'} SOL to @${twitterHandle || 'username'}`}
              </button>
            </div>

            <div className="mt-8 p-4 bg-blue-900 bg-opacity-50 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-2">How it works:</h3>
              <ol className="text-gray-300 space-y-1 text-sm">
                <li>1. 🚀 Powered by Alchemy RPC for maximum reliability</li>
                <li>2. 💼 1% fee automatically collected for platform development</li>
                <li>3. 🎁 99% is held safely for the recipient</li>
                <li>4. 🐦 Recipient logs in with Twitter to verify identity</li>
                <li>5. 💸 Platform transfers the SOL directly to their wallet</li>
                <li>6. ✅ Fast, reliable, and working perfectly!</li>
              </ol>
            </div>
          </div>
        )}

        {/* Claim Tab */}
        {activeTab === 'claim' && (
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Claim Your Solana</h2>
            
            {/* Twitter Login */}
            {!authToken ? (
              <div className="mb-6">
                <button
                  onClick={handleTwitterLogin}
                  disabled={isLoading}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <Twitter size={20} />
                  <span>{isLoading ? 'Redirecting to Twitter...' : 'Login with Twitter to View Claims'}</span>
                </button>
                <p className="text-gray-400 text-sm text-center mt-2">
                  Secure Twitter OAuth integration
                </p>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-green-900 bg-opacity-50 border border-green-700 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-green-400" size={16} />
                  <span className="text-green-300 text-sm">
                    Authenticated as @{userProfile?.username}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="ml-auto text-green-300 hover:text-white text-sm underline"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Withdrawal Address */}
            {authToken && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Withdrawal Address
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={withdrawalAddress}
                    onChange={(e) => setWithdrawalAddress(e.target.value)}
                    placeholder="Enter your Solana wallet address"
                    className="flex-1 px-4 py-3 bg-white bg-opacity-20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  />
                  {wallet.connected && (
                    <button
                      onClick={() => setWithdrawalAddress(wallet.publicKey)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                    >
                      Use Wallet
                    </button>
                  )}
                  <button
                    onClick={updateWithdrawalAddress}
                    disabled={!withdrawalAddress}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm"
                  >
                    Save
                  </button>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Where your SOL will be sent (after 1% platform fee)
                </div>
              </div>
            )}

            {/* Pending Claims */}
            {authToken && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Your Pending Claims</h3>
                
                {claims.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No pending claims found</p>
                    <p className="text-sm">Ask someone to send you SOL via your Twitter handle</p>
                  </div>
                ) : (
                  claims.map((claim) => (
                    <ClaimCard 
                      key={claim._id}
                      claim={claim}
                      onWithdraw={handleWithdrawal}
                      isLoading={isLoading}
                      withdrawalAddress={withdrawalAddress}
                      formatDate={formatDate}
                      getDaysUntilExpiry={getDaysUntilExpiry}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6">User Profile</h2>
            
            {!authToken ? (
              <div className="text-center py-8 text-gray-400">
                <User size={48} className="mx-auto mb-4 opacity-50" />
                <p>Please login with Twitter to view your profile</p>
              </div>
            ) : userProfile ? (
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  {userProfile.profileImageUrl ? (
                    <img 
                      src={userProfile.profileImageUrl} 
                      alt="Profile" 
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <User size={64} className="text-gray-400" />
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {userProfile.displayName || userProfile.username}
                    </h3>
                    <p className="text-gray-300">@{userProfile.username}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        userProfile.verified ? 'bg-blue-600' : 'bg-gray-600'
                      }`}>
                        {userProfile.verified ? 'Real Twitter' : 'Mock Account'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Total Claims</h4>
                    <p className="text-2xl font-bold text-white">{userProfile.claimsCount || 0}</p>
                  </div>
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Total Claimed</h4>
                    <p className="text-2xl font-bold text-white">{(userProfile.totalClaimed || 0).toFixed(4)} SOL</p>
                    <p className="text-xs text-gray-400 mt-1">After platform fees</p>
                  </div>
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Last Login</h4>
                    <p className="text-lg font-semibold text-white">
                      {new Date(userProfile.lastLogin).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {userProfile.walletAddress && (
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-300 mb-2">Saved Withdrawal Address</h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-mono text-sm">
                        {userProfile.walletAddress.slice(0, 8)}...{userProfile.walletAddress.slice(-8)}
                      </span>
                      <button
                        onClick={() => copyToClipboard(userProfile.walletAddress)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p>Loading profile...</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-gray-400 text-sm">
          <div className="bg-white bg-opacity-5 rounded-lg p-4">
            <h4 className="font-semibold mb-2">✅ Production Ready - Powered by Alchemy!</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div>🚀 Alchemy RPC for maximum reliability</div>
              <div>🐦 Real Twitter OAuth authentication</div>
              <div>💰 Secure escrow system</div>
              <div>⚡ Instant withdrawals</div>
              <div>💼 1% fee supports development</div>
              <div>🎁 99% goes to recipients</div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Network: Mainnet • Production Ready • Alchemy Powered
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simplified Claim Card component
const ClaimCard = ({ claim, onWithdraw, isLoading, withdrawalAddress, formatDate, getDaysUntilExpiry }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-600';
      case 'processing': return 'bg-blue-600';
      case 'completed': return 'bg-green-600';
      case 'failed': return 'bg-red-600';
      case 'expired': return 'bg-gray-600';
      default: return 'bg-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="text-green-400" size={20} />;
      case 'processing': return <Clock className="text-blue-400" size={20} />;
      case 'failed': return <AlertCircle className="text-red-400" size={20} />;
      default: return null;
    }
  };

  return (
    <div className="bg-white bg-opacity-20 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-white">
          <div className="font-semibold text-lg flex items-center space-x-2">
            <span>{(claim.amount / 1_000_000_000).toFixed(4)} SOL</span>
            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(claim.status)}`}>
              {claim.status || 'pending'}
            </span>
          </div>
          <div className="text-sm text-gray-300">
            From: {claim.senderAddress?.slice(0, 6)}...{claim.senderAddress?.slice(-4)}
          </div>
          
          {/* Simplified Fee Information */}
          {claim.feeInfo && (
            <div className="text-xs text-gray-400 mt-1">
              <div>You receive: {claim.feeInfo.recipientAmountSOL.toFixed(4)} SOL (after 1% fee)</div>
            </div>
          )}
          
          <div className="text-xs text-gray-400">
            Claim ID: {claim.claimId}
          </div>
        </div>
        {getStatusIcon(claim.status)}
      </div>

      <div className="flex items-center justify-between text-sm text-gray-300 mb-3">
        <div className="flex items-center space-x-2">
          <Clock size={14} />
          <span>Expires in {getDaysUntilExpiry(claim.expiryDate)} days</span>
        </div>
        <div>
          Created: {formatDate(claim.createdAt)}
        </div>
      </div>

      {claim.status === 'pending' && (
        <div className="space-y-2">
          <div className="text-blue-400 text-sm p-2 bg-blue-900 bg-opacity-50 rounded">
            💰 Ready to withdraw {(claim.amount / 1_000_000_000).toFixed(4)} SOL (after 1% platform fee).
          </div>
          
          <button
            onClick={() => onWithdraw(claim)}
            disabled={!withdrawalAddress || isLoading}
            className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200"
          >
            {isLoading ? 'Processing Withdrawal...' : 
             !withdrawalAddress ? 'Set Withdrawal Address First' :
             `Withdraw ${(claim.amount / 1_000_000_000).toFixed(4)} SOL`}
          </button>
        </div>
      )}
      
      {claim.status === 'processing' && (
        <div className="text-center">
          <span className="text-blue-400 font-semibold">🔄 Processing withdrawal...</span>
          <div className="text-xs text-gray-400 mt-1">
            This may take a few moments. Please wait.
          </div>
        </div>
      )}
      
      {claim.status === 'completed' && (
        <div className="text-center">
          <span className="text-green-400 font-semibold">✅ Withdrawal Completed</span>
          <div className="text-xs text-gray-400 mt-1">
            SOL has been transferred to your wallet!
          </div>
        </div>
      )}

      {claim.status === 'failed' && (
        <div className="text-center">
          <span className="text-red-400 font-semibold">❌ Withdrawal Failed</span>
          {claim.errorMessage && (
            <div className="text-xs text-gray-400 mt-1">
              Error: {claim.errorMessage}
            </div>
          )}
          {claim.retryCount < 3 && (
            <button
              onClick={() => onWithdraw(claim)}
              disabled={isLoading}
              className="mt-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-1 rounded text-sm"
            >
              Retry Withdrawal
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SolanaTwitterClaimer;